import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Autocomplete,
  MenuItem,
  IconButton,
  Paper,
  Stack,
  Chip,
  Divider,
  Card,
  CardContent,
  Collapse,
  Tooltip,
  CircularProgress,
  Alert,
  Fade,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AdminQuizAPIs from "../../../utilities/api/quiz/AdminQuizAPIs";
import AdminModuleAPIs from "../../../utilities/api/tutorials/AdminModuleAPIs";
import { toast } from "react-toastify";
// ─── Section wrapper ──────────────────────────────────────────────────────────
const Section = ({ title, subtitle, children }) => (
  <Paper variant="outlined" sx={{ borderRadius: 3, p: { xs: 2, sm: 3 }, mb: 3 }}>
    <Box mb={2.5}>
      <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
      )}
    </Box>
    <Divider sx={{ mb: 2.5 }} />
    {children}
  </Paper>
);

// ─── Question Card ────────────────────────────────────────────────────────────
const QuestionCard = ({ q, i, updateQuestion, updateOption, removeQuestion }) => {
  const [open, setOpen] = useState(true);

  return (
    <Card variant="outlined" sx={{ mb: 1.5, borderRadius: 2, overflow: "hidden" }}>
      {/* Header */}
      <Box
        display="flex" alignItems="center" gap={1.5}
        px={{ xs: 1.5, sm: 2 }} py={1.5} bgcolor="grey.50"
        sx={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => setOpen((o) => !o)}
      >
        <Box sx={{
          width: 28, height: 28, borderRadius: 1.5,
          bgcolor: "primary.main", color: "primary.contrastText",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "0.75rem", fontWeight: 700, flexShrink: 0,
        }}>
          {i + 1}
        </Box>

        <Typography variant="body2" fontWeight={600} flex={1} noWrap color="text.primary">
          {q.question || (
            <Box component="span" sx={{ color: "text.disabled", fontStyle: "italic" }}>
              Untitled question…
            </Box>
          )}
        </Typography>

        <Tooltip title="Remove question">
          <IconButton
            size="small" color="error"
            onClick={(e) => { e.stopPropagation(); removeQuestion(i); }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <ExpandMoreIcon
          fontSize="small"
          sx={{
            color: "text.disabled",
            transition: "transform 0.2s",
            transform: open ? "rotate(0deg)" : "rotate(-90deg)",
          }}
        />
      </Box>

      {/* Body */}
      <Collapse in={open}>
        <Divider />
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack spacing={2}>
            <TextField
              label="Question Text"
              fullWidth multiline minRows={2} size="small"
              value={q.question}
              placeholder="Enter your question…"
              onChange={(e) => updateQuestion(i, "question", e.target.value)}
            />

            {/* Correct Answer */}
            <TextField
              select label="Correct Answer" size="small"
              value={q.correct_answer}
              onChange={(e) => updateQuestion(i, "correct_answer", e.target.value)}
            >
              {["A", "B", "C", "D"].map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>

            {/* Options */}
            <Box>
              <Typography
                variant="caption" color="text.secondary" fontWeight={600}
                textTransform="uppercase" letterSpacing={0.6} display="block" mb={1}
              >
                Options
              </Typography>
              <Box
                display="grid"
                gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                gap={1.5}
              >
                {["A", "B", "C", "D"].map((key) => (
                  <TextField
                    key={key}
                    label={`Option ${key}`} size="small"
                    value={q.options[key]}
                    onChange={(e) => updateOption(i, key, e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <Typography variant="caption" color="primary" fontWeight={700} mr={0.5}>
                          {key}
                        </Typography>
                      ),
                    }}
                    sx={{
                      ...(q.correct_answer === key && {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "primary.main",
                          borderWidth: 2,
                        },
                      }),
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Collapse>
    </Card>
  );
};

// ─── Main Form ────────────────────────────────────────────────────────────────
const AddQuizForm = ({ onBack }) => {
  const [modules, setModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [quizInfo, setQuizInfo] = useState({
    title: "",
    estimated_time_req: "",
    passing_score: "",
    attempts_allowed: 1,
    due_date: "",
    isActive: true,
  });

  const [questions, setQuestions] = useState([
    { question: "", type: "MCQ", options: { A: "", B: "", C: "", D: "" }, correct_answer: "" },
  ]);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const res = await AdminModuleAPIs.ModuleListGet();
        setModules(res || []);
      } catch (err) {
        console.error("Failed to load modules", err);
      }
    };
    fetchModules();
  }, []);

  const setInfo = (field) => (e) =>
    setQuizInfo({ ...quizInfo, [field]: e.target.value });

  const addQuestion = () =>
    setQuestions((prev) => [
      ...prev,
      { question: "", type: "MCQ", options: { A: "", B: "", C: "", D: "" }, correct_answer: "" },
    ]);

  const removeQuestion = (index) =>
    setQuestions((prev) => prev.filter((_, i) => i !== index));

  const updateQuestion = (index, field, value) => {
    const copy = [...questions];
    copy[index][field] = value;
    setQuestions(copy);
  };

  const updateOption = (index, key, value) => {
    const copy = [...questions];
    copy[index].options[key] = value;
    setQuestions(copy);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await AdminQuizAPIs.CreateQuiz({
        ...quizInfo,
        total_questions: questions.length,
        modules: selectedModules.map((m) => m.id),
        questions,
      });
toast.success("Quiz created successfully ", {
  position: "top-right",
  autoClose: 2000,
});

setTimeout(() => {
  onBack();
}, 1200);
    } catch (err) {
      console.error(err);
      toast.error("Failed to create quiz");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box minHeight="100vh" bgcolor="grey.50">

      {/* ── Sticky Top Bar ── */}
      <Paper
        elevation={0} square
        sx={{
          position: "sticky", top: 0, zIndex: 10,
          borderBottom: "1px solid", borderColor: "divider",
          px: { xs: 1.5, sm: 3 },
          py: { xs: 1, sm: 0 },
          minHeight: 56,
          display: "flex",
          alignItems: "center",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          gap: { xs: 1, sm: 2 },
        }}
      >
        {/* Back button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          size="small"
          color="inherit"
          sx={{ color: "text.secondary", fontWeight: 600, flexShrink: 0 }}
        >
          Back
        </Button>

        <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />

        {/* Title — hidden on xs, shown inline on sm+ */}
        <Typography
          variant="subtitle1" fontWeight={600} flex={1} noWrap
          sx={{ display: { xs: "none", sm: "block" } }}
        >
          Create Quiz
        </Typography>

        {/* Actions row */}
        <Box display="flex" alignItems="center" gap={1} ml={{ xs: "auto", sm: 0 }} flexShrink={0}>
          {/* Short chip on mobile, full label on desktop */}
          <Chip
            label={`${questions.length} Q`}
            size="small" variant="outlined" color="primary"
            sx={{ fontWeight: 700, display: { xs: "flex", sm: "none" } }}
          />
          <Chip
            label={`${questions.length} question${questions.length !== 1 ? "s" : ""}`}
            size="small" variant="outlined" color="primary"
            sx={{ fontWeight: 700, display: { xs: "none", sm: "flex" } }}
          />

          {/* Cancel hidden on mobile (bottom bar covers it) */}
          <Button
            variant="outlined" size="small"
            onClick={onBack} disabled={submitting}
            sx={{ display: { xs: "none", sm: "inline-flex" } }}
          >
            Cancel
          </Button>

          <Button
            variant="contained" size="small"
            onClick={handleSubmit} disabled={submitting}
            startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {submitting ? "Saving…" : "Save"}
          </Button>
        </Box>

        {/* Mobile-only title on second line */}
        <Typography
          variant="subtitle2" fontWeight={600}
          sx={{ display: { xs: "block", sm: "none" }, width: "100%", pb: 0.25 }}
        >
          Create Quiz
        </Typography>
      </Paper>

      {/* ── Success Toast ── */}
      
      {/* ── Content ── */}
      <Box maxWidth={820} mx="auto" px={{ xs: 1.5, sm: 3 }} py={{ xs: 2.5, sm: 4 }}>

        {/* ── Quiz Details ── */}
        <Section title="Quiz Details" subtitle="Basic configuration for this quiz">
          <Stack spacing={2}>
            <TextField
              label="Quiz Title" fullWidth size="small"
              value={quizInfo.title} onChange={setInfo("title")}
              placeholder="e.g. JavaScript Fundamentals"
            />

            {/* 2-col on sm+, single col on xs */}
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
              <TextField
                label="Estimated Time (min)" type="number" size="small"
                value={quizInfo.estimated_time_req} onChange={setInfo("estimated_time_req")}
                inputProps={{ min: 1 }}
              />
              <TextField
                label="Passing Score (%)" type="number" size="small"
                value={quizInfo.passing_score} onChange={setInfo("passing_score")}
                inputProps={{ min: 1, max: 100 }}
              />
            </Box>

            <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
              <TextField
                label="Attempts Allowed" type="number" size="small"
                value={quizInfo.attempts_allowed} onChange={setInfo("attempts_allowed")}
                inputProps={{ min: 1 }}
              />
              <TextField
                label="Due Date" type="datetime-local" size="small"
                InputLabelProps={{ shrink: true }}
                value={quizInfo.due_date} onChange={setInfo("due_date")}
              />
            </Box>
          </Stack>
        </Section>

        {/* ── Module Assignment ── */}
        <Section title="Assign Modules" subtitle="Link this quiz to one or more course modules">
          <Autocomplete
            multiple
            options={modules}
            getOptionLabel={(o) => o.name || ""}
            value={selectedModules}
            onChange={(e, val) => setSelectedModules(val)}
            renderInput={(params) => (
              <TextField {...params} label="Modules" size="small" placeholder="Search modules…" />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option.id}
                  label={option.name}
                  size="small" color="primary" variant="outlined"
                  sx={{ fontWeight: 600 }}
                  {...getTagProps({ index })}
                />
              ))
            }
          />
        </Section>

        {/* ── Questions ── */}
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={2}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Questions</Typography>
            <Typography variant="caption" color="text.secondary">
              {questions.length} question{questions.length !== 1 ? "s" : ""} · Click header to collapse
            </Typography>
          </Box>
          <Button
            variant="contained" startIcon={<AddIcon />} size="small"
            onClick={addQuestion} sx={{ flexShrink: 0 }}
          >
            {/* Full label on sm+, icon-only label on xs */}
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Add Question</Box>
            <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>Add</Box>
          </Button>
        </Box>

        {questions.map((q, i) => (
          <QuestionCard
            key={i} q={q} i={i}
            updateQuestion={updateQuestion}
            updateOption={updateOption}
            removeQuestion={removeQuestion}
          />
        ))}

        {/* ── Bottom Actions ── */}
        <Box
          display="flex"
          flexDirection={{ xs: "column-reverse", sm: "row" }}
          justifyContent="flex-end"
          gap={1.5}
          mt={2}
        >
          <Button
            variant="outlined" onClick={onBack} disabled={submitting}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Cancel
          </Button>
          <Button
            variant="contained" onClick={handleSubmit} disabled={submitting}
            startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {submitting ? "Saving…" : "Save Quiz"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AddQuizForm;